<template>
    <Toast />
    <ConfirmDialog />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <Button icon="pi pi-arrow-left" label="Historial" class="p-button-text p-0 mb-2" @click="$router.push('/dashboard/cierre-caja/historial')" />
                        <h5 class="m-0" v-if="closing">Cierre #{{ closing.id }}</h5>
                    </div>
                    <div class="flex gap-2 mt-2 md:mt-0" v-if="closing">
                        <Button v-if="closing.pdfUrl" label="Descargar PDF" icon="pi pi-file-pdf" class="p-button-outlined" @click="downloadPdf" />
                        <Button v-if="isAdmin && closing.status === 'OPEN'" label="Anular" icon="pi pi-times" class="p-button-danger p-button-outlined" @click="confirmVoid" />
                    </div>
                </div>

                <ProgressBar v-if="loading" mode="indeterminate" style="height: 4px" />

                <div v-if="!loading && closing">
                    <div class="grid">
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Estado</div>
                            <Tag v-if="closing.status === 'OPEN'" value="Vigente" severity="success" />
                            <Tag v-else value="Anulado" severity="danger" />
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Resultado</div>
                            <Tag :value="resultLabel(closing.result)" :severity="resultSeverity(closing.result)" />
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Fecha</div>
                            <div class="font-medium">{{ formatDate(closing.createdAt) }}</div>
                        </div>
                        <div class="col-12 md:col-3">
                            <div class="text-color-secondary text-sm">Realizado por</div>
                            <div class="font-medium">{{ closing.creator }} <Tag :value="closing.creatorRol" severity="info" class="ml-1" /></div>
                        </div>
                    </div>

                    <div class="grid mt-2">
                        <div class="col-12 md:col-6">
                            <div class="text-color-secondary text-sm">Período</div>
                            <div>
                                <span v-if="closing.periodStart">{{ formatDate(closing.periodStart) }}</span>
                                <span v-else><i>Primer cierre</i></span>
                                <i class="pi pi-arrow-right mx-2 text-xs"></i>
                                <span>{{ formatDate(closing.periodEnd) }}</span>
                            </div>
                        </div>
                    </div>

                    <Message v-if="closing.status === 'VOIDED'" severity="error" :closable="false" class="mt-3">
                        Cierre anulado el {{ formatDate(closing.voidedAt) }}
                        <span v-if="closing.voidedBy"> por {{ closing.voidedBy }}</span>.
                        <span v-if="closing.voidReason">Motivo: {{ closing.voidReason }}</span>
                    </Message>

                    <h6 class="mt-4">Detalle por cuenta</h6>
                    <DataTable :value="closing.lines" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column field="accountName" header="Cuenta" />
                        <Column field="moneyType" header="Tipo">
                            <template #body="{ data }">{{ moneyTypeLabel(data.moneyType) }}</template>
                        </Column>
                        <Column header="Modo">
                            <template #body="{ data }">{{ evaluationModeLabel(data.evaluationMode) }}</template>
                        </Column>
                        <Column header="Teórico">
                            <template #body="{ data }">{{ formatMoney(data.theoreticalValue) }}</template>
                        </Column>
                        <Column header="Práctico">
                            <template #body="{ data }">{{ formatMoney(data.actualValue) }}</template>
                        </Column>
                        <Column header="Diferencia">
                            <template #body="{ data }">
                                <span :class="resultClassByDiff(data.difference)">{{ formatMoney(data.difference) }}</span>
                            </template>
                        </Column>
                        <Column header="Resultado">
                            <template #body="{ data }">
                                <Tag :value="resultLabel(data.result)" :severity="resultSeverity(data.result)" />
                            </template>
                        </Column>
                    </DataTable>

                    <div class="surface-100 border-round p-3 mt-3">
                        <div class="grid">
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Total teórico</div>
                                <div class="font-bold text-xl">{{ formatMoney(closing.totalTheoretical) }}</div>
                            </div>
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Total práctico</div>
                                <div class="font-bold text-xl">{{ formatMoney(closing.totalActual) }}</div>
                            </div>
                            <div class="col-4 text-center">
                                <div class="text-color-secondary text-sm">Diferencia global</div>
                                <div class="font-bold text-xl" :class="resultClassByDiff(closing.totalDifference)">{{ formatMoney(closing.totalDifference) }}</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="closing.notes" class="mt-3">
                        <h6>Notas</h6>
                        <div class="surface-100 border-round p-3">{{ closing.notes }}</div>
                    </div>

                    <div v-if="closing.summary" class="mt-4">
                        <h6>Resumen del período</h6>
                        <div class="grid">
                            <div class="col-12 md:col-6">
                                <div class="surface-100 border-round p-3">
                                    <div class="font-medium">Ventas</div>
                                    <div class="text-color-secondary text-sm mb-2">
                                        {{ formatMoney(closing.summary.salesTotal) }} - {{ closing.summary.salesCount }} doc.
                                    </div>
                                    <div v-for="(info, code) in (closing.summary.salesByPaymentMethod || {})" :key="'s_' + code" class="flex justify-content-between text-sm">
                                        <span>{{ paymentMethodLabel(code) }}</span>
                                        <span class="font-medium">{{ formatMoney(info.total) }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 md:col-6">
                                <div class="surface-100 border-round p-3">
                                    <div class="font-medium">Compras</div>
                                    <div class="text-color-secondary text-sm mb-2">
                                        {{ formatMoney(closing.summary.purchasesTotal) }} - {{ closing.summary.purchasesCount }} doc.
                                    </div>
                                    <div v-for="(info, code) in (closing.summary.purchasesByPaymentMethod || {})" :key="'p_' + code" class="flex justify-content-between text-sm">
                                        <span>{{ paymentMethodLabel(code) }}</span>
                                        <span class="font-medium">{{ formatMoney(info.total) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Dialog v-model:visible="voidDialog" header="Anular cierre" modal :style="{ width: '420px' }">
            <p>Indique la razón de la anulación. Esta acción no se puede deshacer.</p>
            <Textarea v-model="voidReason" rows="3" class="w-full" />
            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="voidDialog = false" />
                <Button label="Anular" class="p-button-danger" :loading="voiding" @click="doVoid" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';

export default {
    name: 'CashClosingDetail',
    data() {
        return {
            loading: false,
            voiding: false,
            voidDialog: false,
            voidReason: '',
            closing: null
        };
    },
    created() {
        if (!this.isAdmin) {
            this.$router.replace('/dashboard');
            return;
        }
        const id = this.$route.query.id;
        if (!id) {
            this.$router.replace('/dashboard/cierre-caja/historial');
            return;
        }
        this.load(id);
    },
    computed: {
        isAdmin() {
            // Espejo del decorador backend: historial/detalle/anular es admin-only.
            return this.$can('cashClosingManage');
        }
    },
    methods: {
        async load(id) {
            this.loading = true;
            try {
                const r = await httpRequest.getCashClosing(id);
                if (r.status === 200 && r.data) {
                    this.closing = r.data;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        confirmVoid() {
            this.voidReason = '';
            this.voidDialog = true;
        },
        async doVoid() {
            this.voiding = true;
            try {
                const r = await httpRequest.voidCashClosing(this.closing.id, this.voidReason);
                if (r.status === 200 && r.data) {
                    this.closing = r.data;
                    this.voidDialog = false;
                    this.$toast.add({ severity: 'success', summary: 'Cierre anulado', life: 3000 });
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo anular', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.voiding = false;
            }
        },
        downloadPdf() {
            if (this.closing && this.closing.pdfUrl) {
                window.open(this.$baseURL + this.closing.pdfUrl, '_blank');
            }
        },
        formatDate(s) {
            if (!s) return '-';
            try { return new Date(s).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; }
        },
        formatMoney(v) {
            const n = Number(v || 0);
            return '$ ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        moneyTypeLabel(code) {
            return { CASH: 'Efectivo', TRANSFER: 'Transferencia', CARD: 'Tarjeta' }[code] || code;
        },
        evaluationModeLabel(code) {
            return { TOTAL_BALANCE: 'Saldo total', COLLECTED_INFLOWS: 'Cobros del período' }[code] || code;
        },
        paymentMethodLabel(code) {
            return {
                CASH: 'Efectivo', TRANSFER: 'Transferencia', DEBIT_CARD: 'Tarjeta Débito',
                CREDIT_CARD: 'Tarjeta Crédito', CHECK: 'Cheque', MIXED: 'Mixto', CREDIT_NOTE: 'Nota de crédito', NONE: 'Sin método'
            }[code] || code;
        },
        resultLabel(code) {
            return { BALANCED: 'Cuadrado', OVERAGE: 'Sobrante', SHORTAGE: 'Faltante' }[code] || code;
        },
        resultSeverity(code) {
            return { BALANCED: 'success', OVERAGE: 'info', SHORTAGE: 'danger' }[code] || 'info';
        },
        resultClassByDiff(diff) {
            const n = Number(diff || 0);
            if (n === 0) return 'text-green-500 font-medium';
            return n > 0 ? 'text-blue-500 font-medium' : 'text-red-500 font-medium';
        }
    }
};
</script>
